import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, UserSearchIcon, SendIcon, CheckCircle2Icon, CheckIcon } from 'lucide-react';
type Step = 'search' | 'find' | 'outreach';
export function OwnerFinderDemo() {
  const [activeStep, setActiveStep] = useState<Step>('search');
  const [searchPhase, setSearchPhase] = useState<'loading' | 'done'>('loading');
  const [findPhase, setFindPhase] = useState<'loading' | 'done'>('loading');
  const [outreachPhase, setOutreachPhase] = useState<'loading' | 'done'>('loading');
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const findTimerRef = useRef<NodeJS.Timeout | null>(null);
  const outreachTimerRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    startSearch();
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (findTimerRef.current) clearTimeout(findTimerRef.current);
      if (outreachTimerRef.current) clearTimeout(outreachTimerRef.current);
    };
  }, []);
  const startSearch = () => {
    setSearchPhase('loading');
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearchPhase('done'), 1200);
  };
  const startFind = () => {
    setFindPhase('loading');
    if (findTimerRef.current) clearTimeout(findTimerRef.current);
    findTimerRef.current = setTimeout(() => setFindPhase('done'), 1400);
  };
  const startOutreach = () => {
    setOutreachPhase('loading');
    if (outreachTimerRef.current) clearTimeout(outreachTimerRef.current);
    outreachTimerRef.current = setTimeout(() => setOutreachPhase('done'), 1500);
  };
  const handleTabSwitch = (step: Step) => {
    setActiveStep(step);
    if (step === 'search') startSearch();
    if (step === 'find') startFind();
    if (step === 'outreach') startOutreach();
  };
  return <section className="py-24 bg-white" data-id="element-5778">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5779">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-5780">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-5781">
            See the local business{' '}
            <span className="text-blue-600" data-id="element-5782">owner finder</span> in action
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-5783">
            Search local businesses, find verified business owner contacts, and
            launch outreach — all from one platform.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-14" data-id="element-5784">
          <button onClick={() => handleTabSwitch('search')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'search' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-5785">
            <SearchIcon className="w-4 h-4" data-id="element-5786" />
            Search businesses
          </button>
          <button onClick={() => handleTabSwitch('find')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'find' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-5787">
            <UserSearchIcon className="w-4 h-4" data-id="element-5788" />
            Find owner contacts
          </button>
          <button onClick={() => handleTabSwitch('outreach')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'outreach' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-5789">
            <SendIcon className="w-4 h-4" data-id="element-5790" />
            Launch outreach
          </button>
        </div>

        <AnimatePresence mode="wait" data-id="element-5791">
          {activeStep === 'search' && <motion.div key="search" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-5792">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-5793">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-5794">
                  Search businesses
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-5795">
                  Find local businesses to target.
                </p>
                <div className="space-y-5" data-id="element-5796">
                  <div data-id="element-5797">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-5798">
                      Industry
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-5799">
                      Restaurants
                    </div>
                  </div>
                  <div data-id="element-5800">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-5801">
                      Location
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-5802">
                      Austin, TX
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-5803">
                  {searchPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-5804">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-5805" />
                      Searching database...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-5806">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-5807" />
                      1,240 businesses found
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-5808">
                {searchPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-5809">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-5810" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-5811">
                      Querying local business database...
                    </p>
                  </div>}
                {searchPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-5812">
                    <div className="flex items-center justify-between mb-6" data-id="element-5813">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-5814">
                        Business results
                      </h3>
                    </div>
                    <div className="space-y-3" data-id="element-5815">
                      {['Austin Taco Co.', 'Riverside Cafe', 'Texas BBQ Joint'].map((name, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-5 shadow-sm flex items-center justify-between" data-id="element-5816">
                          <div data-id="element-5817">
                            <h4 className="text-base font-bold text-slate-900" data-id="element-5818">
                              {name}
                            </h4>
                            <p className="text-xs text-slate-500" data-id="element-5819">
                              Austin, TX • Restaurant
                            </p>
                          </div>
                          <CheckCircle2Icon className="w-5 h-5 text-green-500" data-id="element-5820" />
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'find' && <motion.div key="find" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-5821">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-5822">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-5823">
                  Find owner contacts
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-5824">
                  Select target decision-makers.
                </p>
                <div className="space-y-5" data-id="element-5825">
                  <div data-id="element-5826">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-5827">
                      Target titles
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-5828">
                      Owner, Founder
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-5829">
                  {findPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-5830">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-5831" />
                      Finding business owners...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-5832">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-5833" />
                      Owner contacts found
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-5834">
                {findPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-5835">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-5836" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-5837">
                      Retrieving verified emails and phones...
                    </p>
                  </div>}
                {findPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-5838">
                    <div className="flex items-center justify-between mb-6" data-id="element-5839">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-5840">
                        Business owner contacts
                      </h3>
                    </div>
                    <div className="space-y-3" data-id="element-5841">
                      {[{
                  name: 'David Smith',
                  title: 'Owner',
                  email: 'david@austintaco.com',
                  phone: '(512) 555-0199'
                }, {
                  name: 'Elena Rodriguez',
                  title: 'Founder',
                  email: 'elena@riversidecafe.com',
                  phone: '(512) 555-0288'
                }, {
                  name: 'Marcus Johnson',
                  title: 'Owner',
                  email: 'marcus@texasbbq.com',
                  phone: '(512) 555-0377'
                }].map((contact, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-5 shadow-sm border border-blue-100" data-id="element-5842">
                          <div className="flex justify-between items-start mb-2" data-id="element-5843">
                            <div data-id="element-5844">
                              <h4 className="text-base font-bold text-slate-900" data-id="element-5845">
                                {contact.name}
                              </h4>
                              <p className="text-xs text-slate-500" data-id="element-5846">
                                {contact.title}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-3" data-id="element-5847">
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded-md" data-id="element-5848">
                              <CheckIcon className="w-3 h-3 text-blue-600" data-id="element-5849" />{' '}
                              {contact.email}
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-md" data-id="element-5850">
                              <CheckIcon className="w-3 h-3 text-green-600" data-id="element-5851" />{' '}
                              {contact.phone}
                            </span>
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'outreach' && <motion.div key="outreach" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-5852">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-5853">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-5854">
                  Launch outreach
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-5855">
                  Send emails to business owners.
                </p>
                <div className="space-y-5" data-id="element-5856">
                  <div data-id="element-5857">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-5858">
                      Campaign name
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700 border-2 border-blue-600" data-id="element-5859">
                      Austin Restaurants
                    </div>
                  </div>
                  <div data-id="element-5860">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-5861">
                      Sender account
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-5862">
                      alex@yourcompany.com
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-5863">
                  {outreachPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-5864">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-5865" />
                      Launching sequence...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-5866">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-5867" />
                      Campaign launched
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-5868">
                {outreachPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-5869">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-5870" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-5871">
                      Preparing emails for local business owners...
                    </p>
                  </div>}
                {outreachPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} className="flex flex-col items-center justify-center min-h-[350px] text-center" data-id="element-5872">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6" data-id="element-5873">
                      <SendIcon className="w-8 h-8 text-green-600" data-id="element-5874" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-5875">
                      Outreach active!
                    </h3>
                    <p className="text-slate-500 mb-8 max-w-sm" data-id="element-5876">
                      Your sequence is now sending to 1,240 verified local
                      business owner emails.
                    </p>
                    <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="w-full px-6 py-3.5 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 transition-colors text-base text-center block max-w-xs mx-auto" data-id="element-5877">
                      View campaign stats
                    </a>
                  </motion.div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}